import { railHelper } from '@/store/details/rail/helper'
import dayjs from 'dayjs'
import CustomParseFormat from 'dayjs/plugin/customParseFormat'
import { Direction, FindFlexFlightsRequest, FindGatewaysRequest, RailTransportType } from '../../../bewotec/openapi'
import { handleError, handleWarning } from '../../../general/errorHandling'
import { DEFAULT_DATE_FORMAT } from '../../../general/helper'
import { COMFORTAI, IND, INDAI } from '../../../general/master-data'
import { useCruiseStore, useDetailsStore, useFlexFlightStore, useRailStore, useUserStore } from '../../../store'
import { appHelper, mapAgencyForRequest } from '../../../store/app/helper'
import { convertToExpitecDirection } from '../rail/helper'
import {
  IFetchRailOption,
  IFetchRailOptionByDirection,
  IFindRailTransportationParameters,
  RailDirection,
} from '../rail/interfaces'
import { flexFlightHelper } from './helper'
import { gatewayId, getDummyBirthdates, mapGateways } from './helper.js'
import {
  FlexFlightCode,
  FlightItem,
  IBirthdates,
  IFlightDirectionItem,
  IMappedGateway,
  ISelectedFilters,
  ITransportationClassItem,
} from './interfaces'

dayjs.extend(CustomParseFormat)

export const resetBirthdates = () => {
  const detailsStore = useDetailsStore()
  const flexFlightStore = useFlexFlightStore()

  const childrenCount: number = detailsStore.paxConfig!.children + detailsStore.paxConfig!.babies
  const birthdates: IBirthdates = {}

  if (childrenCount !== Object.keys(flexFlightStore.birthdates).length) {
    for (let i = 0; i < childrenCount; i++) {
      birthdates['child-' + i] = ''
    }

    flexFlightStore.birthdates = birthdates
  }
}

const validateBirthdates = (birthdates: IBirthdates, minorsCount: number) => {
  if (Object.keys(birthdates).length !== minorsCount) return false

  for (const singleBirthdate of Object.values(birthdates)) {
    const birthdate = dayjs(singleBirthdate, DEFAULT_DATE_FORMAT, true) // strict parsing

    if (!birthdate.isValid() || birthdate.isAfter(dayjs())) {
      return false
    }
  }

  return true
}

export const validateMandatoryFields = async () => {
  const flexFlightStore = useFlexFlightStore()
  const detailsStore = useDetailsStore()

  const minorsCount: number = detailsStore.paxConfig!.children + detailsStore.paxConfig!.babies
  const selectedFilters: ISelectedFilters = flexFlightStore.selectedFilters

  const areBirthdatesSetAndValid: boolean = await validateBirthdates(flexFlightStore.birthdates, minorsCount)

  const isValid =
    !!selectedFilters.gateway &&
    !!selectedFilters.flightDirection &&
    !!selectedFilters.transportationClass &&
    (!minorsCount || (!!minorsCount && areBirthdatesSetAndValid))

  flexFlightStore.isFlexFlightSearchEnabled = isValid
}

export const fetchAvailableGateways = async () => {
  const userStore = useUserStore()
  const flexFlightStore = useFlexFlightStore()
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()

  setFlexFlightMessage(FlexFlightCode.Gateway)

  try {
    const findGatewayRequest: FindGatewaysRequest = {
      agencyId: mapAgencyForRequest(userStore.agencyId),
      cruiseId: cruiseStore.cruise!.id,
      priceModelCode: detailsStore.priceModel!,
    }
    const fetchedGateways = await appHelper.bewotecMicroServiceApi().findGateways(findGatewayRequest)

    if (!!fetchedGateways.length) {
      const mappedGateways = mapGateways(fetchedGateways)

      mappedGateways.sort((a, b) => a.locName.localeCompare(b.locName, 'de'))

      flexFlightStore.availableGateways = mappedGateways
    } else {
      setFlexFlightMessage(FlexFlightCode.GatewayError)
    }
  } catch (error) {
    setFlexFlightMessage(FlexFlightCode.GatewayError)
    handleWarning('no gateways received', { error })
    return
  }
}

export const fetchAvailableFlights = async () => {
  const userStore = useUserStore()
  const flexFlightStore = useFlexFlightStore()
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()

  resetAvailableFlights()

  const filters = flexFlightStore.selectedFilters
  const birthdates = Object.values(flexFlightStore.birthdates)
  const dummyPaxAges = getDummyBirthdates(detailsStore.paxConfig!, birthdates)
  flexFlightStore.isLoading = true
  flexFlightStore.flexFlightMessage = FlexFlightCode.Loading

  try {
    const flexFlightsRequest: FindFlexFlightsRequest = {
      cruiseId: cruiseStore.cruise!.id,
      agencyId: mapAgencyForRequest(userStore.agencyId),
      priceModelCode: detailsStore.priceModel!,
      birthdays: dummyPaxAges,
      gateway: filters.gateway?.locCd ?? '',
      nonstop: filters.nonstop,
      flightClassCode: filters.transportationClass!,
      maxStops: 1,
      carrierCodes: filters.airlines,
      direction: filters.flightDirection!,
      xTrackingIdentifier: flexFlightStore.requestId,
    }

    const flexFlightResponse = await appHelper.bewotecMicroServiceApi().findFlexFlightsRaw(flexFlightsRequest)
    const flexFlights = await flexFlightResponse.value()

    if (flexFlightResponse.raw.headers.get('x-tracking-identifier') !== flexFlightStore.requestId) return

    const flights = !!flexFlights.length ? flexFlightHelper.initFlights(flexFlights) : []
    setFlexFlightMessage(!!flights.length ? FlexFlightCode.None : FlexFlightCode.FlightError)
    flexFlightStore.availableFlights = flights
  } catch (error) {
    handleWarning('no flex flights received', { error })
    setFlexFlightMessage(FlexFlightCode.FlightError)
    flexFlightStore.availableFlights = []
  } finally {
    flexFlightStore.isLoading = false
  }
}

const setFlexFlightMessage = (code: FlexFlightCode) => {
  const flexFlightStore = useFlexFlightStore()

  const selectedFilter: ISelectedFilters = flexFlightStore.selectedFilters

  if (!selectedFilter.gateway) {
    code = FlexFlightCode.Airport
  } else if (!selectedFilter.flightDirection) {
    code = FlexFlightCode.FlightDirection
  } else if (!selectedFilter.transportationClass) {
    code = FlexFlightCode.TransportationClass
  }

  flexFlightStore.flexFlightMessage = code
}

export const selectGateway = (gateway: IMappedGateway | undefined) => {
  const flexFlightStore = useFlexFlightStore()

  flexFlightStore.selectedFilters.gateway = gateway ?? null
  flexFlightStore.selectedFilters.transportationClass = undefined
  flexFlightStore.selectedFilters.flightDirection = undefined

  resetRailAndFlightMessage()

  return
}

export const selectFlightDirection = (direction: IFlightDirectionItem | undefined) => {
  const flexFlightStore = useFlexFlightStore()

  flexFlightStore.selectedFilters.flightDirection = !!direction ? direction.id : undefined
  resetRailAndFlightMessage()

  return
}

export const selectTransportationClass = (transClass: ITransportationClassItem | undefined) => {
  const flexFlightStore = useFlexFlightStore()

  flexFlightStore.selectedFilters.transportationClass = !!transClass ? transClass.id : undefined
  resetRailAndFlightMessage()

  return
}

const resetRailAndFlightMessage = () => {
  const flexFlightStore = useFlexFlightStore()

  flexFlightStore.resetRail()
  flexFlightStore.railDirection = 'none'

  setFlexFlightMessage(FlexFlightCode.None)
}

export const selectFlight = (flight: FlightItem | undefined) => {
  const flexFlightStore = useFlexFlightStore()

  if (!flight) {
    flexFlightStore.selectedFlight = null
    return
  }

  for (const availableFlight of flexFlightStore.availableFlights) {
    if (!('id' in flight)) continue

    if (availableFlight.id === flight.id) {
      flexFlightStore.selectedFlight = availableFlight
      break
    }
  }

  resetRail()
  selectDefaultRailOption()
  return
}

export const setAirlines = (airlines: string[]) => {
  const flexFlightStore = useFlexFlightStore()

  flexFlightStore.selectedFilters.airlines = !airlines.length ? [] : airlines
}

export const fetchRailOptions = async () => {
  const userStore = useUserStore()
  const flexFlightStore = useFlexFlightStore()
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()
  const railStore = useRailStore()

  const cruiseId = cruiseStore.cruise?.id

  if (!cruiseId) {
    flexFlightStore.selectableRailOptions = { error: true }
    handleError({ error: new Error('Cruise ID is missing'), origin: 'flexFlight - fetchRailOptions' })
    return
  }

  const availableRailOptionsByDirection: IFetchRailOptionByDirection = {}

  const findRailTransportationParameters: IFindRailTransportationParameters = {
    cruiseId,
    agencyId: userStore.agencyId,
    priceModel: detailsStore.priceModel!,
    paxConfig: detailsStore.paxConfig!,
    transportationType: RailTransportType.RNF,
    birthdates: Object.values(flexFlightStore.birthdates),
    gateway: gatewayId(flexFlightStore.selectedFlight),
    origin: 'flexFlight',
  }

  const railOptions = await railStore.findRailTransportation(findRailTransportationParameters)

  if (!!railOptions?.length) {
    for (const option of railOptions) {
      if (!availableRailOptionsByDirection[option.direction]) {
        availableRailOptionsByDirection[option.direction] = []
      }

      const mappedOption = railHelper.mapRailOption(option)
      availableRailOptionsByDirection[option.direction]?.push(mappedOption)
    }

    for (const direction of Object.keys(availableRailOptionsByDirection)) {
      const availableRailOptions: IFetchRailOption[] = availableRailOptionsByDirection?.[direction as Direction] ?? []

      availableRailOptions?.sort((a, b) => (a.railClassCode < b.railClassCode ? -1 : 1))
    }

    flexFlightStore.selectableRailOptions = availableRailOptionsByDirection
  } else {
    flexFlightStore.selectableRailOptions = { error: true }
  }
}

export const selectRNFRailDirection = async (railDirection: RailDirection) => {
  const flexFlightStore = useFlexFlightStore()

  if (flexFlightStore.railDirection === railDirection) return

  flexFlightStore.railDirection = railDirection
  flexFlightStore.selectDefaultRailOption()
}

export const selectRailOption = (optionId: string) => {
  const flexFlightStore = useFlexFlightStore()

  const railDirection = flexFlightStore.railDirection

  if (railDirection === 'none') {
    flexFlightStore.resetRail()
    return
  }

  const direction = convertToExpitecDirection(railDirection)
  const selectableRailOptions = flexFlightStore.selectableRailOptions
  const hasSelectableRailOptions = !!selectableRailOptions && !('error' in selectableRailOptions)

  const options = hasSelectableRailOptions ? selectableRailOptions[direction] : []

  if (!!options?.length) {
    for (const option of options) {
      if (!!option && 'id' in option && option.id === optionId) {
        flexFlightStore.selectedRailOption = option
        break
      }
    }
  }
}

export const setInitialRNFDirection = () => {
  const flexFlightStore = useFlexFlightStore()
  const selectableRailOptions = flexFlightStore.selectableRailOptions

  if (flexFlightStore.isNoRailAllowed || 'error' in selectableRailOptions) {
    flexFlightStore.railDirection = 'none'
    return
  }

  switch (flexFlightStore.selectedFlight?.direction) {
    case Direction.RT:
    case Direction.OJ:
      if (!!selectableRailOptions[Direction.RT]?.length) flexFlightStore.railDirection = 'both'
      else if (!!selectableRailOptions[Direction.O]?.length) flexFlightStore.railDirection = 'outbound'
      else if (!!selectableRailOptions[Direction.R]?.length) flexFlightStore.railDirection = 'inbound'
      else flexFlightStore.railDirection = 'none'
      break
    case Direction.O:
      if (!!selectableRailOptions[Direction.O]?.length) flexFlightStore.railDirection = 'outbound'
      else flexFlightStore.railDirection = 'none'
      break
    case Direction.R:
      if (!!selectableRailOptions[Direction.R]?.length) flexFlightStore.railDirection = 'inbound'
      else flexFlightStore.railDirection = 'none'
      break
    // needed, otherwise we will get an error when none of the above is enabled
    // e.g. when the user selects a trans cruise + rnf, but deselects all flights again
    default:
      flexFlightStore.railDirection = 'none'
      break
  }
}

export const selectDefaultRailOption = () => {
  const flexFlightStore = useFlexFlightStore()
  const detailsStore = useDetailsStore()

  const direction = convertToExpitecDirection(flexFlightStore.railDirection)
  const selectableRailOptions = flexFlightStore.selectableRailOptions
  const isNotError = !('error' in selectableRailOptions)

  if ([COMFORTAI].includes(detailsStore.priceModel!) && isNotError) {
    const firstClass = selectableRailOptions[direction]?.find(option => option.railClassCode === '1')
    flexFlightStore.selectedRailOption = firstClass ?? null
    flexFlightStore.selectedRailOptionId = firstClass?.id ?? ''
  }

  if ([IND, INDAI].includes(detailsStore.priceModel!) && isNotError) {
    const secondClass = selectableRailOptions[direction]?.find(option => option.railClassCode === '2')
    flexFlightStore.selectedRailOption = secondClass ?? null
    flexFlightStore.selectedRailOptionId = secondClass?.id ?? ''
  }
}

// filters
export const reset = () => {
  const flexFlightStore = useFlexFlightStore()

  flexFlightStore.resetAvailableFlights()
  flexFlightStore.resetRail()

  flexFlightStore.isFlexFlightSearchEnabled = false
  flexFlightStore.availableGateways = []

  flexFlightStore.selectedFilters.gateway = null
  flexFlightStore.selectedFilters.flightDirection = null
  flexFlightStore.selectedFilters.transportationClass = null
  flexFlightStore.selectedFilters.airlines = []
  flexFlightStore.selectedFilters.nonstop = false
}

export const resetAvailableFlights = () => {
  const flexFlightStore = useFlexFlightStore()

  flexFlightStore.resetRail()

  flexFlightStore.availableFlights = []
  flexFlightStore.selectedFlight = null
  setFlexFlightMessage(FlexFlightCode.None)
  flexFlightStore.railDirection = 'none'
  flexFlightStore.selectableRailOptions = {}
}

export const resetRail = () => {
  const flexFlightStore = useFlexFlightStore()

  flexFlightStore.railErrorMessage = ''
  flexFlightStore.selectedRailOptionId = ''
  flexFlightStore.selectedRailOption = null
}

export default {
  resetBirthdates,
  validateMandatoryFields,
  fetchAvailableGateways,
  fetchAvailableFlights,
  selectGateway,
  selectFlightDirection,
  selectTransportationClass,
  selectDefaultRailOption,
  selectFlight,
  setAirlines,
  fetchRailOptions,
  selectRNFRailDirection,
  selectRailOption,
  setInitialRNFDirection,
  reset,
  resetAvailableFlights,
  resetRail,
}

// --------------- Export for testing Purposes Only ---------------

export const exportForTesting = {
  resetRailAndFlightMessage,
  setFlexFlightMessage,
  validateBirthdates,
}
